import type { Me } from '../types';

export type RoleProfile = 'unavailable' | 'oversight' | 'coordination' | 'care' | 'sales';
export function roleProfile(me?: Pick<Me, 'user' | 'verticals' | 'issue_verticals'> | null): RoleProfile {
  if (!me || me.user.issues_only || me.user.dispatch_only) return 'unavailable';
  if (me.user.intake_coordinator) return 'coordination';
  if (me.user.role === 'admin') return 'oversight';
  if (me.issue_verticals?.some(team => !me.verticals.some(crm => crm.id === team.id))) return 'care';
  return me.verticals.length ? 'sales' : 'unavailable';
}
/**
 * One home page per person, never two. Administrator oversight lives on the
 * Dashboard (team KPIs, pipeline, ownership cleanup); everyone else's work
 * lives on My day. The other page is not offered and redirects home.
 */
export function homeTab(me?: Pick<Me, 'user' | 'verticals' | 'issue_verticals'> | null): 'dashboard' | 'my-day' {
  return roleProfile(me) === 'oversight' ? 'dashboard' : 'my-day';
}
export function defaultDashboardMode(profile: RoleProfile): 'care' | 'sales' {
  return profile === 'coordination' || profile === 'care' ? 'care' : 'sales';
}
export function defaultDayScope(profile: RoleProfile): 'team' | 'mine' {
  return profile === 'oversight' ? 'team' : 'mine';
}
export function dashboardPreferenceKey(userId: number, profile: RoleProfile): string {
  return `toyamacrm:dashboard:v2:${userId}:${profile}`;
}
export function workflowAccessKey(me?: Pick<Me, 'user' | 'verticals' | 'issue_verticals'> | null): string {
  // The same coordinator workflow can have admin or member permissions.
  // Separate cache entries on either security-role or grant changes too.
  return me ? JSON.stringify([me.user.role, roleProfile(me), me.verticals.map(v => v.id).sort((a, b) => a - b), (me.issue_verticals ?? []).map(v => v.id).sort((a, b) => a - b)]) : 'anonymous';
}
export const rolePresentation: Record<RoleProfile, { title: string; subtitle: string }> = {
  unavailable: { title: 'Work overview unavailable', subtitle: 'Use the navigation for your permitted work.' },
  oversight: { title: 'Team oversight', subtitle: 'How every team is doing and what needs an owner.' },
  coordination: { title: 'Intake & coordination', subtitle: 'New enquiries, customer calls, meetings you booked and open issues.' },
  care: { title: 'Customer-care work', subtitle: 'Callbacks and open issues for the teams you support.' },
  sales: { title: 'My customers & sales', subtitle: 'Your customers, deals and follow-ups.' }
};
